import React from 'react';
import { BarChart3, Bell, ClipboardCheck, CreditCard, MessageSquareText, Timer, UserCheck } from 'lucide-react';
import { INotification, TestInvitedPayload } from '../types';

export interface RenderedNotificationDetail {
  label: string;
  value: string;
}

export interface RenderedNotification {
  icon: React.ReactNode;
  title: string;
  body: string;
  href: string | null;
  actionLabel?: string;
  details?: RenderedNotificationDetail[];
}

type Renderer = (notification: INotification) => RenderedNotification;

const toText = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') return value.trim() || null;
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (value instanceof Date) return value.toLocaleString();
  return null;
};

const payloadText = (payload: Record<string, unknown>, key: string): string | null =>
  toText(payload[key]);

const humanizeKey = (key: string): string =>
  key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatDate = (value: unknown): string | null => {
  const raw = toText(value);
  if (!raw) return null;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? raw : date.toLocaleString();
};

const formatAmount = (value: unknown): string | null => {
  const amount = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(amount)) return null;
  return `${new Intl.NumberFormat('uz-UZ').format(amount)} so'm`;
};

const detail = (label: string, value: unknown): RenderedNotificationDetail | null => {
  const text = toText(value);
  return text ? { label, value: text } : null;
};

const compactDetails = (
  details: Array<RenderedNotificationDetail | null | undefined>,
): RenderedNotificationDetail[] => details.filter(Boolean) as RenderedNotificationDetail[];

const testInvitedRenderer: Renderer = (notification) => {
  const payload = notification.payload as Partial<TestInvitedPayload>;
  return {
    icon: <ClipboardCheck className="h-4 w-4 text-muted-foreground" aria-hidden />,
    title: 'You\'ve been invited to a test',
    body: payload.testTitle ? `"${payload.testTitle}" is ready for you to start.` : 'Open your tests to get started.',
    href: '/app/tests',
    actionLabel: 'Open tests',
    details: compactDetails([detail('Test', payload.testTitle)]),
  };
};

const attemptCompletedRenderer: Renderer = (notification) => {
  const payload = notification.payload ?? {};
  const candidateName = payloadText(payload, 'candidateName') ?? 'A candidate';
  const testTitle = payloadText(payload, 'testTitle') ?? 'an assessment';
  const score = payloadText(payload, 'score');
  const maxScore = payloadText(payload, 'maxScore');
  const attemptId = payloadText(payload, 'attemptId');
  return {
    icon: <UserCheck className="h-4 w-4 text-success-600" aria-hidden />,
    title: 'Assessment completed',
    body: `${candidateName} completed ${testTitle}. Review the result and check whether grading needs attention.`,
    href: attemptId ? `/app/results/${attemptId}` : '/app/history',
    actionLabel: 'Review result',
    details: compactDetails([
      detail('Candidate', candidateName),
      detail('Assessment', testTitle),
      score || maxScore ? detail('Score', `${score ?? '-'} / ${maxScore ?? '-'}`) : null,
      detail('Guest attempt', payload.isGuest),
    ]),
  };
};

// Anonymous on purpose, like the author's feedback report: no learner, no attempt.
const testFeedbackRenderer: Renderer = (notification) => {
  const payload = notification.payload ?? {};
  const testTitle = payloadText(payload, 'testTitle') ?? 'a test';
  const testId = payloadText(payload, 'testId');
  const enjoyment = payloadText(payload, 'enjoyment');
  const examSimilarity = payloadText(payload, 'examSimilarity');
  return {
    icon: <MessageSquareText className="h-4 w-4 text-muted-foreground" aria-hidden />,
    title: 'New test feedback',
    body: `A learner left feedback on ${testTitle}.`,
    href: testId ? `/app/tests/${testId}/feedback` : null,
    actionLabel: 'Read feedback',
    details: compactDetails([
      detail('Test', testTitle),
      enjoyment ? detail('Rating', `${enjoyment} / 5`) : null,
      examSimilarity ? detail('Like the real exam', `${examSimilarity} / 5`) : null,
      detail('Left a comment', payload.hasComment),
    ]),
  };
};

const weeklyDigestRenderer: Renderer = (notification) => {
  const payload = notification.payload ?? {};
  return {
    icon: <BarChart3 className="h-4 w-4 text-muted-foreground" aria-hidden />,
    title: 'Weekly analytics digest',
    body: 'Your organization activity summary is ready. Check analytics for trends that may need attention.',
    href: '/app/analytics',
    actionLabel: 'Open analytics',
    details: compactDetails([
      detail('Window', payload.windowDays ? `${payload.windowDays} days` : null),
      detail('Total attempts', payload.totalAttempts),
      detail('Average score', payload.avgScore),
      detail('Pass rate', payload.passRate),
      detail('Active tests', payload.activeTests),
      detail('Total candidates', payload.totalCandidates),
    ]),
  };
};

const billingReminderRenderer: Renderer = (notification) => {
  const payload = notification.payload ?? {};
  const daysLeft = payloadText(payload, 'daysLeft');
  const isTrial = notification.type === 'billing.trial_ending';
  return {
    icon: <Timer className="h-4 w-4 text-warning-600" aria-hidden />,
    title: isTrial ? 'Trial ending soon' : 'Renewal reminder',
    body: daysLeft
      ? isTrial
        ? `Your trial ends in ${daysLeft} day${daysLeft === '1' ? '' : 's'}. You'll move to the Free plan, and tests that use paid features will pause for new students until you upgrade.`
        : `Your subscription ends in ${daysLeft} day${daysLeft === '1' ? '' : 's'}. Review billing to avoid access interruption.`
      : 'Review billing to avoid access interruption.',
    href: '/app/admin/organization/billing',
    actionLabel: 'Open billing',
    details: compactDetails([
      detail('Days left', daysLeft),
      detail('Current period ends', formatDate(payload.currentPeriodEnd)),
    ]),
  };
};

const paymentSucceededRenderer: Renderer = (notification) => {
  const payload = notification.payload ?? {};
  return {
    icon: <CreditCard className="h-4 w-4 text-success-600" aria-hidden />,
    title: 'Payment succeeded',
    body: 'Your payment was received and the subscription was updated.',
    href: '/app/admin/organization/billing',
    actionLabel: 'View billing',
    details: compactDetails([
      detail('Plan', payload.planKey),
      detail('Billing cycle', payload.billingCycle),
      detail('Amount', formatAmount(payload.amount)),
      detail('Provider', payload.provider),
      detail('Period ends', formatDate(payload.periodEnd)),
    ]),
  };
};

const upgradeRequestedRenderer: Renderer = (notification) => {
  const payload = notification.payload ?? {};
  return {
    icon: <CreditCard className="h-4 w-4 text-muted-foreground" aria-hidden />,
    title: 'Upgrade request submitted',
    body: 'An upgrade request was submitted for this organization. Follow up if a plan change needs approval.',
    href: '/app/admin/organization/billing',
    actionLabel: 'Open billing',
    details: compactDetails([
      detail('Requested plan', payload.requestedPlan),
      detail('Requested by', payload.byUserId),
    ]),
  };
};

const fallbackRenderer: Renderer = (notification) => {
  const payload = notification.payload ?? {};
  const body =
    payloadText(payload, 'message') ??
    payloadText(payload, 'description') ??
    payloadText(payload, 'title') ??
    'Expand this notification to review the available details.';
  const details = Object.entries(payload)
    .map(([key, value]) => detail(humanizeKey(key), value))
    .filter(Boolean) as RenderedNotificationDetail[];

  return {
    icon: <Bell className="h-4 w-4 text-muted-foreground" aria-hidden />,
    title: payloadText(payload, 'title') ?? 'New notification',
    body,
    href: null,
    details,
  };
};

const REGISTRY: Record<string, Renderer> = {
  'test.invited': testInvitedRenderer,
  'attempt.completed': attemptCompletedRenderer,
  'test.feedback': testFeedbackRenderer,
  'analytics.weekly_digest': weeklyDigestRenderer,
  'billing.renewal_reminder': billingReminderRenderer,
  'billing.trial_ending': billingReminderRenderer,
  'billing.payment_succeeded': paymentSucceededRenderer,
  'billing.upgrade_requested': upgradeRequestedRenderer,
};

export const renderNotification = (notification: INotification): RenderedNotification => {
  const renderer = REGISTRY[notification.type] ?? fallbackRenderer;
  return renderer(notification);
};
